import { observer } from 'mobx-react-lite'
import { Alert, EnumDropdownItemVariant, type IDropdownItem } from 'shared/ui'
import { Label } from 'shared/ui/Label'
import { RadioOptions } from 'shared/ui/Radio'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { LINK_ROLES_PERMISSIONS_GUIDE } from 'shared/constants/links'
import { UserCardContent } from 'entities/Users'
import { type DowngradeMemberStore } from 'pages/settings/pages/members/ui/actions/downgradeMember/ui/DowngradeMemberModal/store/DowngradeMemberStore'
import styles from './styles.module.scss'

type IDowngradeMemberModalProps = {
  store: DowngradeMemberStore
}

export const DowngradeMemberModal = observer(({ store }: IDowngradeMemberModalProps) => {
  const {
    memberToDowngrade,
    hasPaidSeat,
    hasInboxOwnership,
    deleteSeatOption,
    reassignContactsUserId,
    reassignConversationsUserId,
    reassignInboxOwnershipUserId,
    availableMembersForReassignment,
    seatOptions,
    onSetSeatOption,
    onSetReassignContactsUserId,
    onSetReassignConversationsUserId,
    onSetReassignInboxOwnershipUserId,
  } = store

  if (!memberToDowngrade) return null

  const memberOptions: IDropdownItem[] = availableMembersForReassignment.map((user) => ({
    id: user.id,
    label: user.name,
    variant: EnumDropdownItemVariant.Custom,
    renderOption: () => <UserCardContent item={user} />,
  }))

  const selectedContactsUser = availableMembersForReassignment.find(
    (user) => user.id === reassignContactsUserId
  )
  const selectedConversationsUser = availableMembersForReassignment.find(
    (user) => user.id === reassignConversationsUserId
  )
  const selectedInboxOwnershipUser = availableMembersForReassignment.find(
    (user) => user.id === reassignInboxOwnershipUserId
  )

  return (
    <>
      <Alert
        item={{
          type: 'warning',
          title: 'Downgrade to Observer?',
          desc: 'User will lose access to Conversations, Broadcasts, Triggers and more.',
        }}
        rightActions={[
          {
            variant: 'link',
            type: 'primary',
            text: 'Learn more',
            link: LINK_ROLES_PERMISSIONS_GUIDE,
          },
        ]}
        marginBottom={24}
      />
      <div className={styles.content}>
        {hasPaidSeat && (
          <>
            <SelectField
              size={'medium'}
              labelText={'Reassign owned contacts to'}
              value={reassignContactsUserId ?? undefined}
              setValue={onSetReassignContactsUserId}
              items={memberOptions}
              selectProps={{
                placeholder: 'Select member',
              }}
              btnProps={{
                placeholder: 'Select member',
                text: selectedContactsUser ? (
                  <UserCardContent item={selectedContactsUser} />
                ) : (
                  'Select member'
                ),
                style: {
                  height: '40px',
                  color: !selectedContactsUser ? 'var(--content-neutral-primary)' : undefined,
                },
              }}
              withSearch={true}
            />

            <SelectField
              size={'medium'}
              labelText={'Reassign conversations to'}
              value={reassignConversationsUserId ?? undefined}
              setValue={onSetReassignConversationsUserId}
              items={memberOptions}
              selectProps={{
                placeholder: 'Select member',
              }}
              labelProps={{
                questionTooltipProps: {
                  label: 'Inboxes, AI Agents, Workflows, Campaigns, Keywords, Triggers',
                  width: 212,
                },
              }}
              btnProps={{
                placeholder: 'Select member',
                text: selectedConversationsUser ? (
                  <UserCardContent item={selectedConversationsUser} />
                ) : (
                  'Select member'
                ),
                style: {
                  height: '40px',
                  color: !selectedConversationsUser ? 'var(--content-neutral-primary)' : undefined,
                },
              }}
              withSearch={true}
            />

            {hasInboxOwnership && (
              <SelectField
                size={'medium'}
                labelText={'Reassign inbox ownership to'}
                value={reassignInboxOwnershipUserId ?? undefined}
                setValue={onSetReassignInboxOwnershipUserId}
                items={memberOptions}
                selectProps={{
                  placeholder: 'Select member',
                }}
                btnProps={{
                  placeholder: 'Select member',
                  text: selectedInboxOwnershipUser ? (
                    <UserCardContent item={selectedInboxOwnershipUser} />
                  ) : (
                    'Select member'
                  ),
                  style: {
                    height: '40px',
                    color: !selectedInboxOwnershipUser
                      ? 'var(--content-neutral-primary)'
                      : undefined,
                  },
                }}
                withSearch={true}
              />
            )}

            <div className={styles.section}>
              <Label
                label={{
                  text: 'What would you like to do with this seat?',
                }}
                marginBottom={4}
              />
              <RadioOptions
                options={seatOptions}
                active={deleteSeatOption}
                onChange={onSetSeatOption}
              />
            </div>
          </>
        )}
      </div>
    </>
  )
})
